import { useMutation, useQuery, useQueryClient } from 'react-query'

import { FeatureFlagKeys } from '_hooks/useFeatureFlag'
import { useSettledFeatureFlag } from '_hooks/use-awaited-feature-flag.hooks'
import { getTodayInTimezone } from '@hapana/shared/utils/date.util'

import { useIsPolicyComplianceRefusedByServer } from '_stores/policy-compliance-availability.store'

import { useSiteTimezoneContext } from '_contexts/site-timezone.context'
import { useGetSiteById } from '_hooks/sites.hooks'
import { POLICY_COMPLIANCE_QUERY_KEYS } from '_services/graphql/policy-compliance.queries'
import {
  cancelScheduledSitePolicyRevision,
  createGatingSitePolicy,
  createPaperScanUpload,
  deleteGatingSitePolicy,
  fetchClientAgreementsPage,
  fetchClientPolicyAgreements,
  fetchPaperApprovers,
  getClientPolicyCompliance,
  getClientPolicyRelations,
  getGatingSitePolicies,
  getJurisdictionRules,
  getMyApprovalPinStatus,
  getPolicyComplianceSettings,
  getSitePolicyRevisions,
  publishSitePolicyRevision,
  recordPaperPolicyForm,
  retireJurisdictionRule,
  revokeGuardianAuthority,
  saveJurisdictionRule,
  setMyApprovalPin,
  updateGatingSitePolicy,
  updatePolicyComplianceSettings,
  uploadPaperScan,
  verifyClientAge,
  verifyMyApprovalPin,
  voidPolicyAcceptance,
  withdrawElectronicConsent,
  withdrawGuardianPermission,
  type ApprovalPinStatusData,
  type ClientAgreementsPageVariables,
  type ClientPolicyComplianceData,
  type ClientRelationRow,
  type GatingSitePolicy,
  type JurisdictionRuleInput,
  type JurisdictionRuleRow,
  type PaperPolicyFormSlot,
  type PolicyComplianceSettingsData,
  type SitePolicyRevisionRow,
} from '_services/policy-compliance.service'
import {
  PublishPath,
  buildOutcomeFromPublish,
  buildOutcomeFromRevisions,
  resolvePublishPath,
  selectRevisionTimeline,
  toGatingPolicyInput,
  toIsoDateOnly,
  PublishTiming,
  type GatingPolicyFormValues,
  type PublishOutcome,
} from '_pages/settings/location-settings/location-operations/components/policy-gating/policy-gating.utils'
import {
  WithdrawAction,
  isValidTimezone,
  resolvePolicySiteTimezone,
  type WithdrawFormValues,
} from '_utils/policy-compliance.utils'

import {
  CLIENT_POLICY_DEPENDENT_QUERY_KEYS,
  JURISDICTION_RULE_DEPENDENT_QUERY_KEYS,
  POLICY_COMPLIANCE_SETTINGS_DEPENDENT_QUERY_KEYS,
  POLICY_SETTINGS_DEPENDENT_QUERY_KEYS,
  READINESS_DEPENDENT_QUERY_KEYS,
} from './policy-compliance-query-keys.constants'

export const usePolicyComplianceGate = (): boolean | null => {
  const flag = useSettledFeatureFlag(FeatureFlagKeys.PolicyComplianceGate)
  const isRefusedByServer = useIsPolicyComplianceRefusedByServer()
  return isRefusedByServer ? false : flag
}

export {
  CLIENT_POLICY_DEPENDENT_QUERY_KEYS,
  JURISDICTION_RULE_DEPENDENT_QUERY_KEYS,
  POLICY_COMPLIANCE_SETTINGS_DEPENDENT_QUERY_KEYS,
  POLICY_SETTINGS_DEPENDENT_QUERY_KEYS,
  READINESS_DEPENDENT_QUERY_KEYS,
}

export const usePolicySiteTimezone = (siteId: string | null | undefined) => {
  const contextTimezone = useSiteTimezoneContext()
  const site = useGetSiteById(siteId, { enabled: !isValidTimezone(contextTimezone) }) as {
    data?: { timezone?: string | null }
    isLoading: boolean
  }
  const timezone = resolvePolicySiteTimezone(contextTimezone, site.data?.timezone)
  return {
    timezone,
    siteToday: timezone ? getTodayInTimezone(timezone) : null,
    isLoading: timezone === null && site.isLoading,
  }
}

const useInvalidate = (keys: readonly string[]) => {
  const queryClient = useQueryClient()
  return () => Promise.all(keys.map((key) => queryClient.invalidateQueries([key])))
}

export const useGatingSitePolicies = (siteId: string | null | undefined, enabled: boolean) =>
  useQuery<{ sitePolicies: GatingSitePolicy[] }, unknown, GatingSitePolicy[]>(
    [POLICY_COMPLIANCE_QUERY_KEYS.sitePolicies, { siteId }],
    (context) =>
      getGatingSitePolicies.query(context) as Promise<{ sitePolicies: GatingSitePolicy[] }>,
    { enabled: enabled && Boolean(siteId), select: (data) => data.sitePolicies }
  )

export const useSitePolicyRevisions = (
  siteId: string | null | undefined,
  sitePolicyId: string | null | undefined
) =>
  useQuery<{ sitePolicyRevisions: SitePolicyRevisionRow[] }, unknown, SitePolicyRevisionRow[]>(
    [POLICY_COMPLIANCE_QUERY_KEYS.sitePolicyRevisions, { siteId, sitePolicyId }],
    (context) =>
      getSitePolicyRevisions.query(context) as Promise<{
        sitePolicyRevisions: SitePolicyRevisionRow[]
      }>,
    {
      enabled: Boolean(siteId) && Boolean(sitePolicyId),
      select: (data) => data.sitePolicyRevisions,
    }
  )

type JurisdictionRulesData = {
  equipmentJurisdictionRules: JurisdictionRuleRow[]
  equipmentJurisdictionRulesInForce: JurisdictionRuleRow[]
}

export const useJurisdictionRules = (siteId: string | null | undefined) =>
  useQuery<JurisdictionRulesData>(
    [POLICY_COMPLIANCE_QUERY_KEYS.jurisdictionRules, { siteId }],
    (context) => getJurisdictionRules.query(context) as Promise<JurisdictionRulesData>,
    { enabled: Boolean(siteId), retry: false }
  )

export const usePolicyComplianceSettings = (siteId: string | null | undefined) =>
  useQuery<
    { sitePolicyComplianceSettings: PolicyComplianceSettingsData },
    unknown,
    PolicyComplianceSettingsData
  >(
    [POLICY_COMPLIANCE_QUERY_KEYS.policyComplianceSettings, { siteId }],
    (context) =>
      getPolicyComplianceSettings.query(context) as Promise<{
        sitePolicyComplianceSettings: PolicyComplianceSettingsData
      }>,
    { enabled: Boolean(siteId), select: (data) => data.sitePolicyComplianceSettings }
  )

export const useUpdatePolicyComplianceSettings = () => {
  const invalidate = useInvalidate(POLICY_COMPLIANCE_SETTINGS_DEPENDENT_QUERY_KEYS)
  return useMutation(updatePolicyComplianceSettings, { onSuccess: () => invalidate() })
}

export const useClientPolicyCompliance = (
  siteId: string | null | undefined,
  clientId: string | null | undefined,
  enabled: boolean
) =>
  useQuery<
    { clientPolicyCompliance: ClientPolicyComplianceData },
    unknown,
    ClientPolicyComplianceData
  >(
    [POLICY_COMPLIANCE_QUERY_KEYS.clientPolicyCompliance, { siteId, clientId }],
    (context) =>
      getClientPolicyCompliance.query(context) as Promise<{
        clientPolicyCompliance: ClientPolicyComplianceData
      }>,
    {
      enabled: enabled && Boolean(siteId) && Boolean(clientId),
      select: (data) => data.clientPolicyCompliance,
    }
  )

export const useClientPolicyRelations = (siteId: string, clientId: string, enabled: boolean) =>
  useQuery<{ clientRelations: ClientRelationRow[] }, unknown, ClientRelationRow[]>(
    [POLICY_COMPLIANCE_QUERY_KEYS.clientPolicyRelations, { siteId, clientId }],
    (context) =>
      getClientPolicyRelations.query(context) as Promise<{ clientRelations: ClientRelationRow[] }>,
    { enabled, select: (data) => data.clientRelations }
  )

export const useClientPolicyAgreements = (siteId: string, clientId: string, enabled: boolean) =>
  useQuery(
    [POLICY_COMPLIANCE_QUERY_KEYS.clientPolicyAgreements, { siteId, clientId }],
    () => fetchClientPolicyAgreements({ siteId, clientId }),
    { enabled }
  )

export const useClientAgreementsPage = (
  variables: ClientAgreementsPageVariables,
  enabled: boolean
) =>
  useQuery(
    [POLICY_COMPLIANCE_QUERY_KEYS.clientPolicyAgreements, { ...variables, view: 'page' }],
    () => fetchClientAgreementsPage(variables),
    { enabled, keepPreviousData: true }
  )

export const usePaperApprovers = (siteId: string, enabled: boolean) =>
  useQuery(
    [POLICY_COMPLIANCE_QUERY_KEYS.paperApprovers, { siteId }],
    () => fetchPaperApprovers(siteId),
    { enabled }
  )

export const useMyApprovalPinStatus = (enabled: boolean) =>
  useQuery<{ myApprovalPinStatus: ApprovalPinStatusData }, unknown, ApprovalPinStatusData>(
    [POLICY_COMPLIANCE_QUERY_KEYS.myApprovalPinStatus],
    (context) =>
      getMyApprovalPinStatus.query(context) as Promise<{
        myApprovalPinStatus: ApprovalPinStatusData
      }>,
    { enabled, select: (data) => data.myApprovalPinStatus }
  )

export type SaveGatingPolicyResult = {
  policy: GatingSitePolicy
  outcome: PublishOutcome | null
}

const fetchRevisions = async (siteId: string, sitePolicyId: string) =>
  (
    (await getSitePolicyRevisions.query({
      queryKey: [POLICY_COMPLIANCE_QUERY_KEYS.sitePolicyRevisions, { siteId, sitePolicyId }],
    })) as { sitePolicyRevisions: SitePolicyRevisionRow[] }
  ).sitePolicyRevisions

export const saveGatingPolicy = async (args: {
  siteId: string
  policy: GatingSitePolicy | null
  values: GatingPolicyFormValues
}): Promise<SaveGatingPolicyResult> => {
  const { siteId, policy, values } = args
  const path = resolvePublishPath(policy, values)
  const input = toGatingPolicyInput(values)
  const effectiveFrom =
    values.timing === PublishTiming.SCHEDULED ? toIsoDateOnly(values.effectiveFrom) : null
  if (path === PublishPath.CREATE || !policy) {
    const created = await createGatingSitePolicy({ siteId, input })
    const revisions = await fetchRevisions(siteId, created.sitePolicyId)
    return { policy: created, outcome: buildOutcomeFromRevisions(revisions, null) }
  }
  const previousActive = selectRevisionTimeline(
    await fetchRevisions(siteId, policy.sitePolicyId)
  ).active
  if (path === PublishPath.REPUBLISH) {
    const result = await publishSitePolicyRevision({
      siteId,
      sitePolicyId: policy.sitePolicyId,
      requireReacceptance: values.requireReacceptance,
      effectiveFrom,
    })
    return { policy, outcome: buildOutcomeFromPublish(result, previousActive) }
  }
  if (path === PublishPath.NOTHING) {
    return { policy, outcome: null }
  }
  const { publishResult, ...updated } = await updateGatingSitePolicy({
    id: policy.sitePolicyId,
    siteId,
    input: {
      ...input,
      ...(values.requireReacceptance && { requireReacceptance: true }),
      ...(effectiveFrom && { effectiveFrom }),
    },
  })
  return {
    policy: updated,
    outcome: publishResult ? buildOutcomeFromPublish(publishResult, previousActive) : null,
  }
}

export const useSaveGatingPolicy = () => {
  const invalidate = useInvalidate(POLICY_SETTINGS_DEPENDENT_QUERY_KEYS)
  return useMutation(saveGatingPolicy, { onSuccess: () => invalidate() })
}

export const useDeleteGatingPolicy = () => {
  const invalidate = useInvalidate(POLICY_SETTINGS_DEPENDENT_QUERY_KEYS)
  return useMutation(deleteGatingSitePolicy, { onSuccess: () => invalidate() })
}

export const useCancelScheduledRevision = () => {
  const invalidate = useInvalidate(POLICY_SETTINGS_DEPENDENT_QUERY_KEYS)
  return useMutation(cancelScheduledSitePolicyRevision, { onSuccess: () => invalidate() })
}

export const useSaveJurisdictionRule = () => {
  const invalidate = useInvalidate(JURISDICTION_RULE_DEPENDENT_QUERY_KEYS)
  return useMutation(
    (variables: { siteId: string; ruleId: string | null; input: JurisdictionRuleInput }) =>
      saveJurisdictionRule(variables),
    { onSuccess: () => invalidate() }
  )
}

export const useRetireJurisdictionRule = () => {
  const invalidate = useInvalidate(JURISDICTION_RULE_DEPENDENT_QUERY_KEYS)
  return useMutation(retireJurisdictionRule, { onSuccess: () => invalidate() })
}

export const refetchProfileDateOfBirth = async (clientId: string, siteId: string) => {
  const { resolve } = await import('@hapana/shared/web/services/gqty')
  await resolve(({ query }) => query.client({ id: clientId, siteId })?.client?.dateOfBirth, {
    cachePolicy: 'no-cache',
  })
}

export const useVerifyClientAge = () => {
  const invalidate = useInvalidate(CLIENT_POLICY_DEPENDENT_QUERY_KEYS)
  return useMutation(verifyClientAge, {
    onSuccess: (_result, variables) =>
      Promise.all([invalidate(), refetchProfileDateOfBirth(variables.clientId, variables.siteId)]),
  })
}

export const useSetMyApprovalPin = () => {
  const invalidate = useInvalidate([POLICY_COMPLIANCE_QUERY_KEYS.myApprovalPinStatus])
  return useMutation(setMyApprovalPin, { onSuccess: () => invalidate() })
}

export const useVerifyMyApprovalPin = () => {
  const invalidate = useInvalidate([POLICY_COMPLIANCE_QUERY_KEYS.myApprovalPinStatus])
  return useMutation(verifyMyApprovalPin, { onSettled: () => invalidate() })
}

export const submitPaperPolicyForm = async (args: {
  siteId: string
  clientId: string
  scanFile: File
  approverStaffUserId: string
  approverPin: string
  slots: PaperPolicyFormSlot[]
}) => {
  const ticket = await createPaperScanUpload({ siteId: args.siteId, clientId: args.clientId })
  await uploadPaperScan(ticket, args.scanFile)
  return recordPaperPolicyForm({
    siteId: args.siteId,
    clientId: args.clientId,
    scanUploadId: ticket.uploadId,
    approverStaffUserId: args.approverStaffUserId,
    approverPin: args.approverPin,
    slots: args.slots,
  })
}

export const useRecordPaperPolicyForm = () => {
  const invalidate = useInvalidate(CLIENT_POLICY_DEPENDENT_QUERY_KEYS)
  return useMutation(submitPaperPolicyForm, { onSuccess: () => invalidate() })
}

export const runWithdrawAction = (args: {
  siteId: string
  clientId: string
  values: WithdrawFormValues
}): Promise<unknown> => {
  const { siteId, clientId, values } = args
  const reason = values.reason.trim() === '' ? null : values.reason.trim()
  switch (values.action) {
    case WithdrawAction.WITHDRAW_CONSENT:
      return withdrawElectronicConsent({ siteId, clientId, signerId: values.signerId })
    case WithdrawAction.WITHDRAW_GUARDIAN_PERMISSION:
      return withdrawGuardianPermission({
        siteId,
        clientId,
        guardianSignerId: values.guardianClientId,
        reason,
      })
    case WithdrawAction.VOID_ACCEPTANCE:
      return voidPolicyAcceptance({
        siteId,
        agreementId: values.agreementId,
        reason: reason ?? '',
      })
    case WithdrawAction.REVOKE_GUARDIAN_AUTHORITY:
      return revokeGuardianAuthority({
        siteId,
        clientId,
        guardianClientId: values.guardianClientId,
        reason,
      })
  }
}

export const useWithdrawPolicyAction = () => {
  const invalidate = useInvalidate(CLIENT_POLICY_DEPENDENT_QUERY_KEYS)
  return useMutation(runWithdrawAction, { onSuccess: () => invalidate() })
}
